import { Controller } from "@hotwired/stimulus";

const DRAG_THRESHOLD_PX = 5;

// Vertical sortable for the onboarding placement step.
// A plain click opens the reader; dragging starts only once the pointer moves.
// The dragged card is positioned absolutely inside the list (never fixed): the
// workflow shell has transformed ancestors, which breaks position: fixed.
export default class extends Controller {
  static targets = ["zIndexInput", "previewCard", "interactive"];

  static values = {
    selectedIndex: Number,
    zIndexes: Array,
    zIndex: Number,
  };

  connect() {
    this.onMove = this.pointerDragMove.bind(this);
    this.onUp = this.pointerDragEnd.bind(this);
    this.swallowClick = this.swallowClick.bind(this);
    this.resetDragState();
  }

  disconnect() {
    this.abortDrag();
  }

  preventNativeDrag(event) {
    event.preventDefault();
  }

  pointerDragStart(event) {
    if (event.button != null && event.button !== 0) return;
    if (this.pending || this.dragging) return;
    if (!this.hasPreviewCardTarget || !this.hasInteractiveTarget) return;
    if (!this.previewCardTarget.contains(event.target)) return;

    this.pending = true;
    this.pointerId = event.pointerId;
    this.startY = event.clientY;
    this.lastY = event.clientY;
    this.direction = 0;

    window.addEventListener("pointermove", this.onMove, { passive: false });
    window.addEventListener("pointerup", this.onUp);
    window.addEventListener("pointercancel", this.onUp);
  }

  pointerDragMove(event) {
    if (!this.pending && !this.dragging) return;
    if (event.pointerId !== this.pointerId) return;

    // Keep the browser from selecting text or starting a native link drag.
    event.preventDefault();

    this.frameY = event.clientY;
    if (this.frame) return;

    this.frame = window.requestAnimationFrame(() => {
      this.frame = null;
      this.followPointer(this.frameY);
    });
  }

  followPointer(clientY) {
    if (clientY == null) return;
    if (!this.pending && !this.dragging) return;

    if (Math.abs(clientY - this.lastY) > 0.5) {
      this.direction = Math.sign(clientY - this.lastY);
    }
    this.lastY = clientY;

    if (!this.dragging) {
      if (Math.abs(clientY - this.startY) < DRAG_THRESHOLD_PX) return;
      this.liftCard();
    }

    this.positionCard(clientY);

    const nextIndex = this.indexFromCardEdges();
    if (nextIndex !== this.insertIndex) {
      this.insertIndex = nextIndex;
      this.movePlaceholder(nextIndex);
    }
  }

  liftCard() {
    const card = this.previewCardTarget;
    const list = this.interactiveTarget;
    const cardRect = card.getBoundingClientRect();
    const listRect = list.getBoundingClientRect();

    this.dragging = true;
    this.pending = false;
    this.cardHeight = cardRect.height;
    this.grabOffsetY = this.startY - cardRect.top;

    this.placeholder = document.createElement("div");
    this.placeholder.className = "academy-onboarding-placement__placeholder";
    this.placeholder.setAttribute("aria-hidden", "true");
    this.placeholder.style.height = `${Math.round(cardRect.height)}px`;
    list.insertBefore(this.placeholder, card);

    this.insertIndex = this.placeholderIndex();

    this.element.classList.add("academy-onboarding-placement--dragging");
    card.classList.add("academy-onboarding-placement__preview--floating");
    card.style.width = `${cardRect.width}px`;
    card.style.left = `${cardRect.left - listRect.left}px`;
    card.style.top = `${cardRect.top - listRect.top}px`;

    window.getSelection()?.removeAllRanges();
  }

  positionCard(clientY) {
    const card = this.previewCardTarget;
    const listRect = this.interactiveTarget.getBoundingClientRect();
    const maxTop = Math.max(0, listRect.height - this.cardHeight);
    const top = clientY - this.grabOffsetY - listRect.top;

    card.style.top = `${Math.min(Math.max(top, 0), maxTop)}px`;
  }

  // Compare the dragged card's leading edge with sibling edges: going down we
  // swap as soon as our bottom passes a sibling's top, going up as soon as our
  // top passes a sibling's bottom.
  indexFromCardEdges() {
    const siblings = this.siblings();
    if (siblings.length === 0) return 0;

    const cardRect = this.previewCardTarget.getBoundingClientRect();
    const goingDown = this.direction >= 0;
    const edge = goingDown ? cardRect.bottom : cardRect.top;

    for (let index = 0; index < siblings.length; index += 1) {
      const rect = siblings[index].getBoundingClientRect();
      const boundary = goingDown ? rect.top : rect.bottom;
      if (edge < boundary) return index;
    }

    return siblings.length;
  }

  pointerDragEnd(event) {
    if (!this.pending && !this.dragging) return;
    if (event.pointerId != null && event.pointerId !== this.pointerId) return;

    if (event.type === "pointercancel") {
      this.abortDrag();
      return;
    }

    if (this.frame) {
      window.cancelAnimationFrame(this.frame);
      this.frame = null;
      if (this.frameY != null) this.followPointer(this.frameY);
    }

    const dragged = this.dragging;
    const insertIndex = this.insertIndex;

    this.teardownDrag();

    if (!dragged) return;

    this.applyPosition(insertIndex, this.zIndexesValue[insertIndex]);
    window.addEventListener("click", this.swallowClick, { capture: true, once: true });
  }

  swallowClick(event) {
    event.preventDefault();
    event.stopPropagation();
  }

  abortDrag() {
    this.teardownDrag();
  }

  teardownDrag() {
    if (this.frame) {
      window.cancelAnimationFrame(this.frame);
      this.frame = null;
    }

    window.removeEventListener("pointermove", this.onMove);
    window.removeEventListener("pointerup", this.onUp);
    window.removeEventListener("pointercancel", this.onUp);

    this.element.classList.remove("academy-onboarding-placement--dragging");

    if (this.hasPreviewCardTarget) {
      const card = this.previewCardTarget;
      card.classList.remove("academy-onboarding-placement__preview--floating");
      card.removeAttribute("style");

      if (this.placeholder?.isConnected) {
        this.placeholder.replaceWith(card);
      }
    }

    if (this.placeholder?.isConnected) this.placeholder.remove();

    this.resetDragState();
  }

  placeholderIndex() {
    if (!this.placeholder || !this.hasInteractiveTarget) return 0;

    let index = 0;
    for (const child of this.interactiveTarget.children) {
      if (child === this.placeholder) return index;
      if (child.classList.contains("academy-onboarding-placement__sort-sibling")) index += 1;
    }

    return index;
  }

  movePlaceholder(index) {
    if (!this.placeholder || !this.hasInteractiveTarget) return;
    if (this.placeholderIndex() === index) return;

    const target = this.siblings()[index];

    if (target) {
      this.interactiveTarget.insertBefore(this.placeholder, target);
    } else {
      this.interactiveTarget.appendChild(this.placeholder);
    }
  }

  siblings() {
    if (!this.hasInteractiveTarget) return [];

    return Array.from(this.interactiveTarget.children).filter((element) =>
      element.classList.contains("academy-onboarding-placement__sort-sibling")
    );
  }

  applyPosition(insertIndex, zIndex) {
    if (insertIndex == null || zIndex == null) return;

    this.selectedIndexValue = insertIndex;
    this.zIndexValue = zIndex;

    if (this.hasZIndexInputTarget) {
      this.zIndexInputTarget.value = zIndex;
    }

    if (!this.hasPreviewCardTarget) return;

    const card = this.previewCardTarget;
    card.classList.add("academy-onboarding-placement__preview--landed");
    window.setTimeout(
      () => card.classList.remove("academy-onboarding-placement__preview--landed"),
      280
    );
  }

  resetDragState() {
    this.pending = false;
    this.dragging = false;
    this.pointerId = null;
    this.placeholder = null;
    this.startY = null;
    this.lastY = null;
    this.grabOffsetY = 0;
    this.cardHeight = 0;
    this.direction = 0;
    this.insertIndex = null;
    this.frame = null;
    this.frameY = null;
  }
}
